import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';

// デフォルトの2FA設定状態
const defaultObj = {
    loading: false,
    data: null,
    errorMessage: null,
};

// コード間違い時にモーダル内へインライン表示するメッセージ
const CODE_ERROR_MESSAGE = 'コードが正しくありません。もう一度試してください。';

// 2FA確認の状態型
type Confirm2FA = {
    loading: boolean;
    data: {
        'recovery-codes': string[];
    } | null;
    /** コード間違い時のインライン表示用メッセージ */
    errorMessage: string | null;
};

// フックの戻り値の型定義
type ReturnType = [Confirm2FA, (code: string) => void, () => void]; // eslint-disable-line no-unused-vars

/**
 * 2要素認証の確認を管理するカスタムフック
 * @return {Confirm2FA} multiFactorAuth - 2FA確認の状態（ローディング状態とリカバリーコード）
 * @return {Function} request - 2FA確認リクエストを実行する関数
 * @return {Function} resetState - 状態をリセットする関数
 */
export function useConfirm2FA(): ReturnType {
    const dispatch = useDispatch();
    const mfaConfirmConnection = useAppSelector<MFAConfirmConnection>(
        (state) => state.mfaConfirmConnection,
    );

    const [multiFactorAuth, setMultiFactorAuth] =
        useState<Confirm2FA>(defaultObj);

    // コネクション状態の変化を処理する関数
    const effect = useCallback((connection: MFAConfirmConnection) => {
        const { meta, payload } = connection;
        switch (meta.status) {
            case Actions.statusEnum.SUCCESS:
                setMultiFactorAuth((prev) => ({
                    ...prev,
                    loading: meta.fetch,
                    data: payload.result,
                    errorMessage: null,
                }));
                break;
            case Actions.statusEnum.FAILURE:
                // 確認コード間違いはモーダル内にインライン表示する
                // （IP制限による拒否は 403 → セッションダイアログ側で表示されるためここには来ない）
                setMultiFactorAuth((prev) => ({
                    ...prev,
                    loading: meta.fetch,
                    errorMessage: CODE_ERROR_MESSAGE,
                }));
                break;
            case Actions.statusEnum.ERROR:
            case Actions.statusEnum.REQUEST:
                setMultiFactorAuth((prev) => ({
                    ...prev,
                    loading: meta.fetch,
                    errorMessage: null,
                }));
                break;
            default:
                // デフォルトケースの処理
                break;
        }
    }, []);

    useEffect(() => {
        effect(mfaConfirmConnection);
    }, [mfaConfirmConnection, effect]);

    // 2FA確認コードを送信してリカバリーコードを取得
    const request = useCallback(
        (code: string) => {
            setMultiFactorAuth((prev) => ({
                ...prev,
                loading: true,
            }));

            const confirm = Actions.http.connection.authentication.confirm;
            dispatch(confirm({ code: code }));
        },
        [dispatch],
    );

    // 状態をデフォルト値にリセット
    const resetState = useCallback(() => {
        setMultiFactorAuth({ ...defaultObj });
    }, []);

    return [multiFactorAuth, request, resetState];
}
